import { Vec2 } from 'cc';
import { SpinResult } from '../../DataObject/SlotResult';

export enum RoadmapItemType {
    TEN_SPINS,
    BIG_WIN,
    FEATURE_GAME,
    JACKPOT,
    CURRENT,
    ELLIPSIS,
    ONE_SPIN_NO_WIN,
    ONE_SPIN_WIN,
}

export class RoadmapItemData {
    public type: RoadmapItemType = RoadmapItemType.TEN_SPINS;
    /** whether the item is expanded (out of the normal roadmap size) */
    public expanded = false;
    public spinResults: SpinResult[] = [];

    constructor(
        public readonly position: Vec2,
        public readonly spinCountPerItem: number,
    ) {}

    public get lastSpinResult() {
        return this.spinResults[this.spinResults.length - 1];
    }

    public clone(): RoadmapItemData {
        const clone = new RoadmapItemData(this.position.clone(), this.spinCountPerItem);
        clone.type = this.type;
        clone.expanded = this.expanded;
        clone.spinResults = [...this.spinResults];
        return clone;
    }
}

export class RoadmapChunkData {
    public itemDataList: RoadmapItemData[] = [];

    constructor(public index: number) {}

    public get totalSpinCount() {
        return this.itemDataList.reduce((acc, itemData) => acc + itemData.spinResults.length, 0);
    }
}

export type RoadmapData = RoadmapChunkData[];

export default abstract class BaseRoadmapLayoutStrategy {
    protected roadmapData: RoadmapData;
    protected chunkData: RoadmapChunkData;
    protected itemData: RoadmapItemData;

    constructor(
        protected readonly roadmapSize: Vec2,
        protected readonly maxChunkSize: number,
        protected readonly spinCountPerItem: number,
    ) {}

    public derive(spinResults: SpinResult[]): RoadmapData {
        this.init();
        if (spinResults.length) {
            this.doDerive(spinResults);
        }
        this.trim();
        this.clamp();
        this.onDeriveFinish();
        return this.roadmapData;
    }

    protected init() {
        this.roadmapData = [];
        this.itemData = null;
        this.chunkData = null;
        this.nextChunk();
    }

    protected nextChunk() {
        const chunkIndex = this.chunkData ? this.chunkData.index + 1 : 0;
        this.chunkData = new RoadmapChunkData(chunkIndex);
        this.nextItem(this.calculateFirstPositionOfChunk(chunkIndex));
        this.roadmapData.push(this.chunkData);
    }

    protected nextItem(position: Vec2 = new Vec2()) {
        this.itemData = new RoadmapItemData(position, this.spinCountPerItem);
        this.chunkData.itemDataList.push(this.itemData);
    }

    protected calculateFirstPositionOfChunk(chunkIndex: number): Vec2 {
        return new Vec2(chunkIndex, 0);
    }

    /**
     * make sure the chunk size is not larger than the roadmap size
     */
    protected clamp() {
        if (!this.roadmapData.length) {
            return;
        }
        const lastChunk = this.roadmapData[this.roadmapData.length - 1];
        const lastItemPosition = lastChunk.itemDataList[lastChunk.itemDataList.length - 1].position.x;
        if (lastItemPosition < this.maxChunkSize) {
            return;
        }
        const startChunkIndex = Math.max(0, lastItemPosition - (this.maxChunkSize - 1));
        this.roadmapData = this.roadmapData.slice(startChunkIndex, startChunkIndex + this.maxChunkSize);
        // revise chunk index to start from 0
        this.roadmapData.forEach((chunk, index) => {
            const offset = chunk.index - index;
            chunk.index = index;
            // revise item position relative to the chunk
            chunk.itemDataList.forEach((itemData) => {
                itemData.position.x -= offset;
            });
        });
    }

    /**
     * check if last item and chunk is empty, if so, remove them
     */
    protected trim() {
        if (this.itemData.spinResults.length === 0) {
            this.chunkData.itemDataList.pop();
        }
        if (this.chunkData.itemDataList.length === 0) {
            this.roadmapData.pop();
        }
        this.chunkData = this.roadmapData[this.roadmapData.length - 1] ?? null;
        this.itemData = this.chunkData?.itemDataList[this.chunkData.itemDataList.length - 1] ?? null;
    }

    /**
     * execute after derived data is clamped and trimmed
     */
    protected onDeriveFinish() {}

    protected abstract doDerive(spinResults: SpinResult[]): void;
}
